import { Avatar, FormControlLabel, ImageListItem, ImageList, Switch, TextField, useTheme, Select, MenuItem, InputLabel, FormControl, SelectChangeEvent } from '@mui/material';
import React, { useMemo, useState } from 'react';
import AccountBoxOutlinedIcon from '@mui/icons-material/AccountBoxOutlined';
import { PlanImageItem } from '../types';
import { srcset } from '../common/util';

export enum SimpleFormPropertyType {
    String = 'string',
	Number = 'number',
    Text = 'text',
    Videos = 'videos',
    Image = 'image',
    Images = 'images',
    Toggle = 'toggle',
	Select = 'select',
}

interface TemplatePropertyBase<T> {
	name: keyof T,
	label: string,
	disabled?: boolean,
}

interface TemplatePropertyString<T> extends TemplatePropertyBase<T> {
	type: SimpleFormPropertyType.String,
	required?: boolean,
	placeholder?: string;
	defaultValue?: string;
}

interface TemplatePropertyNumber<T> extends TemplatePropertyBase<T> {
	type: SimpleFormPropertyType.Number,
	required?: boolean,
	placeholder?: string;
	defaultValue?: number;
}

interface TemplatePropertyText<T> extends TemplatePropertyBase<T> {
	type: SimpleFormPropertyType.Text,
	required?: boolean,
	multiline?: boolean,
	rows?: number,
	placeholder?: string;
	defaultValue?: string;
}

interface TemplatePropertyToggle<T> extends TemplatePropertyBase<T> {
	type: SimpleFormPropertyType.Toggle,
}

interface TemplatePropertySelect<T> extends TemplatePropertyBase<T> {
	type: SimpleFormPropertyType.Select,
	options: {label: string, value: any}[],
	defaultValue?: string;
}

interface TemplatePropertyImage<T> extends TemplatePropertyBase<T> {
	type: SimpleFormPropertyType.Image,
}

interface TemplatePropertyImages<T> extends TemplatePropertyBase<T> {
	type: SimpleFormPropertyType.Images,
}

export type SimpleFormTemplateProperty<T> = TemplatePropertyString<T> | TemplatePropertyNumber<T> | TemplatePropertyText<T> | TemplatePropertyToggle<T> | TemplatePropertySelect<T> | TemplatePropertyImage<T> | TemplatePropertyImages<T>;

interface FormFieldProps<T> { 
    property: SimpleFormTemplateProperty<T>;
    value?: any;
    onChange?: (property: string, value: any) => void;
	disabled?: boolean;
	defaultValue?: any;
	placeholder?: string;
}

export function useSimpleForm<T>(template: SimpleFormTemplateProperty<T>[]) {
	const [formData, setFormData] = useState<Partial<T> | null>(null);
	const [initialFormData, setInitialFormData] = useState<Partial<T>>({});

	function resetFormData<T>(data?: Partial<T & {id?: number}>) {
		if (!data) {
			setFormData(null);
			return;
		}
		const newFormData = template.reduce((acc, property) => ({...acc, [property.name]: (data as any)[property.name]}), data.id ? {id: data.id} : {} as Partial<T>);
		setInitialFormData(newFormData as any);
		setFormData(newFormData as any);
	}

	const handleFormFieldChange = (name: string, value: any) => {
		setFormData({
			...formData,
			[name]: value
		});
	};
    
	const hasFormChanged = useMemo(() => {
		if (!initialFormData || !formData) {
			return false;
		}
		return template.find((property) => formData[property.name] !== initialFormData[property.name]) ? true : false;
	}, [formData, initialFormData]);

	const getFormData = (e: React.FormEvent<HTMLFormElement>) => {
		e.preventDefault();
		if (!formData) {
			return null;
		}
		const nativeFormData = new FormData(e.target as HTMLFormElement);
		const booleanValues = template.filter((property) => property.type === SimpleFormPropertyType.Toggle).reduce((acc, property) => ({
			...acc,
			[property.name]: nativeFormData.get(property.name as string) === 'on',
		}), {});
		const selectValues = template.filter((property) => property.type === SimpleFormPropertyType.Select).reduce((acc, property) => ({
			...acc,
			[property.name]: formData[property.name],
		}), {});
		
		return({
			...Object.fromEntries(nativeFormData),
			...booleanValues,
			...selectValues,
		});
	};

	return {formData, handleFormFieldChange, resetFormData, hasFormChanged, getFormData};
}

export function SimpleFormField<T>({ property, value, defaultValue, onChange }: FormFieldProps<T>) {
	const theme = useTheme();

	const handleChange = (e: React.ChangeEvent<any>) => {
		if (onChange) {
			onChange(e.target.name, e.target.value);
		}
	};

	const handleSelectChange = (e: SelectChangeEvent<any>) => {
		if (onChange) {
			onChange(e.target.name, e.target.value);
		}
	};

	switch (property.type) {
	case SimpleFormPropertyType.String: {
		return <TextField 
			required={property.required} 
			autoComplete='off' 
			name={property.name as string} 
			label={property.label} 
			placeholder={property.placeholder}
			variant="filled" 
			fullWidth 
			value={value}
			onChange={handleChange}
			disabled={property.disabled}
			defaultValue={defaultValue}
		/>;
	}
	case SimpleFormPropertyType.Number: {
		return <TextField 
			autoComplete='off' 
			type='number' 
			name={property.name as string} 
			label={property.label} 
			placeholder={property.placeholder}
			variant="filled" 
			fullWidth 
			value={value} 
			onChange={handleChange}
			required={property.required} 
			disabled={property.disabled}
			defaultValue={defaultValue}
		/>;
	}
	case SimpleFormPropertyType.Text: {
		return <TextField
			name={property.name as string}
			id="outlined-textarea"
			label={property.label}
			placeholder={property.placeholder}
			multiline={property.multiline}
			variant='filled'
			rows={property.rows}
			fullWidth
			value={value}
			onChange={handleChange}
			required={property.required}
			disabled={property.disabled}
			defaultValue={defaultValue}
		/>;
	}
	case SimpleFormPropertyType.Toggle: {
		return <FormControlLabel
			sx={{userSelect: 'none'}}
			control={<Switch 
				name={property.name as string}
				checked={value}
				defaultChecked={defaultValue}
				disabled={property.disabled}
				onChange={onChange ? (e: React.ChangeEvent<HTMLInputElement>) => onChange(property.name as string, e.target.checked) : undefined} />} 
			label={property.label} 
		/>;
	}
	case SimpleFormPropertyType.Image: {
		return <Avatar src={value ?? undefined} sx={{ bgcolor: theme.palette.action.hover, color: theme.palette.text.primary, width: 196, height: 196, fontSize: 40, borderRadius: '5px' }} variant="square">
			<AccountBoxOutlinedIcon color='disabled' fontSize='large' />
		</Avatar>;
	}
	case SimpleFormPropertyType.Select: {
		return <FormControl fullWidth>
			<InputLabel id={`${String(property.name)}-label`}>{property.label}</InputLabel>
			<Select
				labelId={`${String(property.name)}-label`}
				id={`${String(property.name)}-select`}
				name={property.name as string}
				label={property.label}
				defaultValue={defaultValue}
				disabled={property.disabled}
				variant='filled'
				onChange={handleSelectChange}
			>
				{property.options.map((option) => (
					<MenuItem key={option.value} value={option.value}>
						{option.label}
					</MenuItem>
				))}
			</Select>
		</FormControl>;
	}
	case SimpleFormPropertyType.Images: {
		const values: PlanImageItem[] = value ?? [];
		return <ImageList
			variant="quilted"
			cols={4}
			rowHeight={100}
			sx={{m:0}}
		>
			{values.map((productImage, i) => {
				const colsRows = i === 0 ? 4 : 2;
				return (
					<ImageListItem key={productImage.imgUrl} cols={colsRows} rows={colsRows} sx={{borderRadius: 2, overflow: 'hidden', boxShadow: 3}}>
						<img
							{...srcset(productImage.imgUrl, 250, colsRows * 1, colsRows * 1)}
							// alt={productImage.alt}
							loading="lazy"
							style={{overflow:'hidden'}}
						/>
					</ImageListItem>
				);})}
		</ImageList>;
	}
	default: {
		return <span>{JSON.stringify(value)}</span>;
		// throw new Error('invalid property type')
	}  
	}
}
